import { useFirebase } from '~/composables/use-firebase';
import { customAlphabet } from 'nanoid/non-secure';
import { alphanumeric } from 'nanoid-dictionary';
import { useLoader } from '~/composables/use-loader';
import { hasFileUrlExtensions } from '~/pages/_components/content-details/content.utils';
import {
  AlertType,
  buildDownloadFileName,
  getFileNameFromUrl,
  useToast,
} from '@exfluential/ui';

const nanoid = customAlphabet(alphanumeric, 20);

export const getUrlFileExtension = (fileUrl: string) => {
  const { pathname } = new URL(fileUrl);
  return pathname.split('.').pop();
};

export const buildSocialFileDownloadName = (
  title: string,
  fileUrl: string,
  index: number,
  total: number,
): string => {
  const sourceFileName = getFileNameFromUrl(fileUrl);
  const name = title.trim();
  if (!name) return sourceFileName;
  return buildDownloadFileName(
    total > 1 ? `${name} (${index + 1})` : name,
    sourceFileName,
  );
};

export const downloadFile = async (
  fileUrl: string,
  fileName?: string,
): Promise<boolean> => {
  let isDownloaded = false;

  await useLoader({
    action: async () => {
      const res = await fetch(fileUrl);
      if (!res.ok) {
        throw new Error(`File download failed with status ${res.status}`);
      }
      const blob = await res.blob();

      const { saveAs } = await import('file-saver');
      saveAs(blob, fileName ?? `file.${getUrlFileExtension(fileUrl)}`);
      isDownloaded = true;
    },
    onError: () => {
      useToast().add({
        type: AlertType.ERROR,
        title:
          'Something went wrong while trying to process a file download request. Please try again later.',
      });
    },
  });

  return isDownloaded;
};

export const createStorageFilePath = (fileName: string) => {
  const { auth } = useFirebase();

  const extension = fileName.slice(fileName.lastIndexOf('.') + 1);

  return `${auth.currentUser?.uid}/${nanoid()}.${extension}`;
};

export const isFileImage = (fileUrl: string) => {
  const extensions = ['.png', '.jpg', '.jpeg', '.gif'];
  return hasFileUrlExtensions(extensions, fileUrl);
};

export const isFileVideo = (fileUrl: string) => {
  const extensions = ['.mp4', '.mov', '.ogg', '.webm'];
  return hasFileUrlExtensions(extensions, fileUrl);
};
